/** Modelo de dominio de NovaBerry. */

/** "despacho" es una identidad compartida, no una persona: quien reparte
 *  entra como "Despachos" para ver la ruta del día, sin pasar por el
 *  resto de la app. */
export type Rol = 'admin' | 'vendedor' | 'despacho'

export interface Usuario {
  id: string
  nombre: string
  /** Solo informativo: NovaBerry no tiene cuentas por persona, así que
   *  el correo ya no identifica a nadie ni sirve para entrar. */
  email?: string
  telefono?: string
  rol: Rol
  activo: boolean
  /** Porcentaje de comisión sobre el total vendido (5 = 5%). Cada
   *  vendedor tiene la suya; se define en Vendedores. */
  comision: number
  creado: string
  ultimaConexion?: string
  /** A diferencia del resto del material de marketing mensual (que es
   *  el mismo para todos), la tarjeta de presentación es propia de
   *  cada vendedor —se sube desde su perfil, no desde Ajustes. */
  tarjetaPresentacion?: string
}

export type Categoria = 'frutas' | 'miel' | 'avena' | 'otros'

export const CATEGORIAS: { valor: Categoria; etiqueta: string }[] = [
  { valor: 'frutas', etiqueta: 'Frutas' },
  { valor: 'miel', etiqueta: 'Miel' },
  { valor: 'avena', etiqueta: 'Avena' },
  { valor: 'otros', etiqueta: 'Otros' },
]

export type Unidad = 'kg' | 'g' | 'unidad' | 'caja' | 'bandeja' | 'litro'

export interface Producto {
  id: string
  codigo: string
  nombre: string
  categoria: Categoria
  /** Peso o contenido por unidad de venta, en la unidad indicada. */
  peso: number
  unidad: Unidad
  /** Solo el administrador la ve y la edita — nunca se muestra a un
   *  vendedor, ni en pantalla ni en exportaciones. */
  precioCosto: number
  precioSugerido: number
  /** Piso de venta a clientes mayoristas (pastelerías, etc.). A
   *  diferencia de precioCosto, esto SÍ lo ve el vendedor: es la
   *  referencia para no vender más barato de la cuenta. */
  precioMinimoMayorista?: number
  /** Piso de venta cuando la cantidad es chica (menos de 3 kilos). */
  precioMinimoDetalle?: number
  /** Piso de venta mayorista cuando el pedido de este producto llega a
   *  10 kilos o más —más barato que `precioMinimoMayorista` porque el
   *  volumen lo justifica. Solo aplica a clientes empresa. */
  precioMinimoMayoristaVolumen?: number
  stock: number
  /** Unidades ya comprometidas en pedidos sin despachar. */
  reservado: number
  /** Umbral propio; si es null se usa el umbral global de ajustes. */
  stockMinimo: number | null
  foto?: string
  codigoBarras?: string
  activo: boolean
  /** Posición fija en el catálogo (más chico = más arriba). Los que no
   *  tienen valor van al final, ordenados por nombre. */
  ordenCatalogo?: number
  creado: string
}

/** Un lote es una entrada de stock con vencimiento propio. Se vende
 *  primero el más antiguo (FEFO: first expired, first out). */
export interface Lote {
  id: string
  productoId: string
  codigo: string
  cantidad: number
  cantidadInicial: number
  vencimiento?: string
  costo: number
  proveedor?: string
  /** Quién recibió físicamente la mercadería —no siempre es quien
   *  carga los datos en el sistema. */
  recibidoPor?: string
  ingresado: string
}

export type TipoMovimiento = 'ingreso' | 'venta' | 'ajuste' | 'devolucion' | 'merma'

export interface Movimiento {
  id: string
  productoId: string
  tipo: TipoMovimiento
  /** Positiva suma al stock, negativa resta. */
  cantidad: number
  stockResultante: number
  loteId?: string
  proveedor?: string
  costo?: number
  /** Quién recibió físicamente la mercadería (solo ingresos). */
  recibidoPor?: string
  referenciaId?: string
  usuarioId: string
  usuarioNombre: string
  observaciones?: string
  fecha: string
}

export type TipoCliente = 'empresa' | 'particular'

export type RubroEmpresa =
  | 'pasteleria'
  | 'restaurante'
  | 'cafeteria'
  | 'hotel'
  | 'distribuidor'
  | 'otro'

export const RUBROS: { valor: RubroEmpresa; etiqueta: string }[] = [
  { valor: 'pasteleria', etiqueta: 'Pastelería' },
  { valor: 'restaurante', etiqueta: 'Restaurante' },
  { valor: 'cafeteria', etiqueta: 'Cafetería' },
  { valor: 'hotel', etiqueta: 'Hotel' },
  { valor: 'distribuidor', etiqueta: 'Distribuidor' },
  { valor: 'otro', etiqueta: 'Otro' },
]

export interface Cliente {
  id: string
  tipo: TipoCliente
  /** Razón social si es empresa, nombre de pila si es particular. */
  nombre: string
  apellido?: string
  rubro?: RubroEmpresa
  rut?: string
  direccion?: string
  comuna?: string
  telefono?: string
  email?: string
  contacto?: string
  observaciones?: string
  lat?: number
  lng?: number
  /** Cada cuántos días este cliente vuelve a pedir. Se usa para avisarle
   *  al vendedor que ya toca volver a ofrecerle, sin esperar a que el
   *  cliente llame primero. */
  frecuenciaPedidoDias?: number
  creado: string
}

export type MetodoPago = 'efectivo' | 'transferencia' | 'tarjeta' | 'credito'

export const METODOS_PAGO: { valor: MetodoPago; etiqueta: string; icono: string }[] = [
  { valor: 'efectivo', etiqueta: 'Efectivo', icono: 'Banknote' },
  { valor: 'transferencia', etiqueta: 'Transferencia', icono: 'ArrowLeftRight' },
  { valor: 'tarjeta', etiqueta: 'Tarjeta', icono: 'CreditCard' },
  { valor: 'credito', etiqueta: 'Crédito', icono: 'CalendarClock' },
]

export type EstadoPedido = 'pendiente' | 'preparando' | 'despachada' | 'entregada' | 'anulada'

export const ESTADOS_PEDIDO: { valor: EstadoPedido; etiqueta: string }[] = [
  { valor: 'pendiente', etiqueta: 'Pendiente' },
  { valor: 'preparando', etiqueta: 'Preparando' },
  { valor: 'despachada', etiqueta: 'Despachado' },
  { valor: 'entregada', etiqueta: 'Entregado' },
  { valor: 'anulada', etiqueta: 'Anulado' },
]

/** Antes de salir a repartir, el despachador llama o escribe a cada
 *  cliente del día. Solo salen a la ruta los confirmados; los otros dos
 *  casos se corren al siguiente día de reparto. */
export type ConfirmacionCliente = 'confirmado' | 'no_contesto' | 'no_confirmado'

export const CONFIRMACIONES: {
  valor: ConfirmacionCliente
  etiqueta: string
  detalle: string
}[] = [
  { valor: 'confirmado', etiqueta: 'Confirmado', detalle: 'El cliente espera el pedido hoy' },
  { valor: 'no_contesto', etiqueta: 'No contestó', detalle: 'Se corre al próximo día de reparto' },
  { valor: 'no_confirmado', etiqueta: 'No confirmó', detalle: 'Se corre al próximo día de reparto' },
]

export type DiaDespacho = 'martes' | 'jueves' | 'especial'

export const DIAS_DESPACHO: { valor: DiaDespacho; etiqueta: string }[] = [
  { valor: 'especial', etiqueta: 'Primer despacho' },
  { valor: 'martes', etiqueta: 'Martes' },
  { valor: 'jueves', etiqueta: 'Jueves' },
]

export interface LineaVenta {
  productoId: string
  /** Copia del nombre y código al momento de vender: el producto puede
   *  cambiar de nombre después y la venta no debe cambiar con él. */
  nombre: string
  codigo: string
  cantidad: number
  precioUnitario: number
  costoUnitario: number
  subtotal: number
  loteId?: string
}

export interface Venta {
  id: string
  folio: string
  clienteId: string
  clienteNombre: string
  clienteTipo: TipoCliente
  usuarioId: string
  usuarioNombre: string
  lineas: LineaVenta[]
  total: number
  costoTotal: number
  utilidad: number
  /** Comisión del vendedor sobre esta venta, calculada con la tasa que
   *  tenía al momento de venderla. Si la tasa cambia después, esta
   *  venta no cambia con ella — igual que el costo de cada línea. */
  comision: number
  metodoPago: MetodoPago
  estado: EstadoPedido
  /** Solo para ventas a crédito. */
  pagado: boolean
  vencimientoPago?: string
  /** Cuándo se marcó como entregada. Sirve para avisar si un cliente
   *  lleva mucho tiempo sin recibir un pedido nuevo. */
  fechaEntrega?: string
  /** Martes, jueves o un día especial —lo elige el vendedor al vender. */
  diaDespacho?: DiaDespacho
  /** La fecha concreta que le corresponde (el próximo martes/jueves,
   *  o la fecha especial elegida). Es lo que usa Despachos para armar
   *  la ruta del día. */
  fechaDespacho?: string
  /** Si no se llena, Despachos usa la dirección del cliente. */
  direccionDespacho?: string
  /** El despachador tiene que confirmar que recibió y revisó cada
   *  pedido antes de ver la hoja de ruta del día —así no sale a
   *  repartir algo que en realidad no está listo o tiene un problema. */
  revisadoDespacho?: boolean
  /** Si el despachador encuentra algo mal (falta stock, la dirección no
   *  cuadra, etc.) lo anota acá en vez de confirmar a ciegas. */
  erroDespacho?: string
  /** Resultado del contacto con el cliente antes de salir a repartir.
   *  Solo los confirmados entran a la ruta del día. Admite null a
   *  propósito: al guardar en Supabase, un `undefined` se pierde en el
   *  JSON y la columna se quedaría con el valor anterior. */
  confirmacionCliente?: ConfirmacionCliente | null
  /** Cuántas veces se corrió de día por no lograr confirmar. Sirve para
   *  que un cliente inubicable no quede rebotando en silencio para
   *  siempre. */
  vecesPospuesto?: number
  observaciones?: string
  fecha: string
}

export interface Devolucion {
  id: string
  ventaId: string
  ventaFolio: string
  productoId: string
  productoNombre: string
  cantidad: number
  monto: number
  motivo: string
  /** Si el producto vuelve vendible se repone al stock. */
  reponeStock: boolean
  usuarioId: string
  usuarioNombre: string
  fecha: string
}

export type AccionBitacora =
  | 'inicio_sesion'
  | 'cierre_sesion'
  | 'crear'
  | 'editar'
  | 'eliminar'
  | 'venta'
  | 'ingreso_stock'
  | 'devolucion'
  | 'exportar'
  | 'ajuste'

export interface Bitacora {
  id: string
  usuarioId: string
  usuarioNombre: string
  accion: AccionBitacora
  entidad: string
  entidadId?: string
  detalle: string
  fecha: string
}

export interface Ajustes {
  id: string
  /** Umbral global de "poco stock" cuando el producto no define el suyo. */
  stockMinimoGlobal: number
  /** Días de aviso antes del vencimiento de un lote. */
  diasAvisoVencimiento: number
  /** Días de plazo por defecto para ventas a crédito. */
  diasCredito: number
  nombreEmpresa: string
  rutEmpresa: string
  direccionEmpresa: string
  telefonoEmpresa: string
  emailEmpresa: string
  moneda: string
  /** Los precios de venta de la app son netos —sin IVA—; este
   *  porcentaje es el que se suma para mostrar el total con IVA en
   *  Gastos y en los reportes financieros. Chile: 19%. */
  porcentajeIva: number
  /** Categorías de gasto que el administrador arma a su gusto (Gastos
   *  fijos, Publicidad, etc.). Empiezan con algunas típicas. */
  categoriasGasto: string[]
  /** Punto de partida de la ruta de despacho. */
  bodegaDireccion: string
  bodegaLat: number
  bodegaLng: number
  /** Quién reparte hoy —se muestra arriba en Despachos, con botón de
   *  llamar/WhatsApp directo. */
  despachadorNombre: string
  despachadorTelefono: string
  /** Tarifario "tipo Uber" para los despachos de "Primer despacho": los
   *  que no caen en la ruta programada del martes o jueves, así que el
   *  despachador los hace como una salida propia y le corresponde un
   *  pago aparte. Tarifa = base + (km en línea recta desde la bodega ×
   *  tarifa por km), igual que el cálculo de una tarifa de app de
   *  transporte. */
  tarifaDespachoBase: number
  tarifaDespachoPorKm: number
  /** Material de apoyo del mes: el administrador lo sube desde el
   *  perfil y cada vendedor lo descarga desde el suyo. Se guardan como
   *  URL de datos (base64), igual que la foto de un producto —no hay
   *  bucket de archivos configurado. */
  marketingMapaCobertura?: string
  marketingPreciosDetalle?: string
  marketingPreciosMayorista?: string
  marketingTarjetaPresentacion?: string
  marketingOfertasSemana?: string
}

export type TipoGasto = 'fijo' | 'variable' | 'publicidad' | 'compra' | 'otro'

export const TIPOS_GASTO: { valor: TipoGasto; etiqueta: string }[] = [
  { valor: 'fijo', etiqueta: 'Gasto fijo' },
  { valor: 'variable', etiqueta: 'Gasto variable' },
  { valor: 'publicidad', etiqueta: 'Publicidad' },
  { valor: 'compra', etiqueta: 'Compra' },
  { valor: 'otro', etiqueta: 'Otro' },
]

/** Un gasto de la empresa: arriendo, luz, publicidad, una compra
 *  puntual, lo que sea. Existe para que el administrador no tenga que
 *  llevar esto aparte en una planilla. */
export interface Gasto {
  id: string
  categoria: string
  tipo: TipoGasto
  descripcion: string
  /** Lo que realmente se pagó (el monto total, con IVA si corresponde). */
  monto: number
  /** Si el gasto tiene factura con IVA, se puede descontar como
   *  crédito fiscal; si es boleta o no tiene IVA, no. */
  afectoIva: boolean
  proveedor?: string
  observaciones?: string
  usuarioId: string
  usuarioNombre: string
  fecha: string
}

/** Estado de stock derivado, con el color del espectro que le toca. */
export type EstadoStock = 'disponible' | 'poco' | 'agotado'

export interface Notificacion {
  id: string
  tipo: 'stock_bajo' | 'agotado' | 'vencimiento' | 'cobranza' | 'seguimiento'
  titulo: string
  detalle: string
  enlace?: string
  fecha: string
  leida: boolean
}
